import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  Upload,
  Trash2,
  X,
  Image,
  Video,
  Music,
  FileText,
  Eye,
  Plus,
  Settings
} from "lucide-react";
import DragDropUpload from "../components/DragDropUpload";

const AdminMediaGallery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_type: "image"
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/login");
      return;
    }
    fetchMedia();
  }, [user, navigate]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/media?committee=mosque-dawah`);
      setMedia(response.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description || "");
      data.append("media_type", formData.media_type);
      data.append("committee", "mosque-dawah");
      data.append("file", file);

      await axios.post(`${API}/media`, data, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Media uploaded successfully!");
      fetchMedia();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/media/${mediaId}`, { withCredentials: true });
      toast.success("Media deleted!");
      fetchMedia();
      setSelectedMedia(null);
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const resetForm = () => {
    setShowUploadForm(false);
    setFormData({ title: "", description: "", media_type: "image" });
    setFile(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredMedia = activeTab === "all" 
    ? media 
    : media.filter(m => m.media_type === activeTab);

  const mediaCounts = {
    all: media.length,
    image: media.filter(m => m.media_type === "image").length,
    video: media.filter(m => m.media_type === "video").length,
    audio: media.filter(m => m.media_type === "audio").length,
    document: media.filter(m => m.media_type === "document").length
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
        <div className="container-custom py-8">
          <Link to="/admin" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Admin Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-xs font-medium mb-2">
                <Settings className="w-3 h-3" />
                Mosque and Da'wah Committee
              </div>
              <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="media-gallery-title">
                Media Gallery Management
              </h1>
              <p className="text-white/80 mt-1">Upload and manage images, videos, audio, and documents</p>
            </div>
            <Button 
              onClick={() => setShowUploadForm(true)} 
              className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl shadow-lg" 
              data-testid="upload-new-media-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Media
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Upload Form */}
        {showUploadForm && (
          <Card className="mb-8 p-6 rounded-3xl shadow-lg border-0">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-gray-900">Upload New Media</h3>
              <Button variant="ghost" size="icon" onClick={resetForm} className="rounded-full">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Media title"
                    required
                    className="h-12 rounded-xl"
                    data-testid="media-upload-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium">Media Type *</Label>
                  <Select value={formData.media_type} onValueChange={(v) => { setFormData({ ...formData, media_type: v }); setFile(null); }}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="media-upload-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Description (Optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the media..."
                  rows={2}
                  className="rounded-xl resize-none"
                  data-testid="media-upload-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Select File *</Label>
                <DragDropUpload
                  onFileSelect={setFile}
                  currentFile={file}
                  mediaType={formData.media_type}
                  maxSize={50 * 1024 * 1024}
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6" disabled={uploading || !file} data-testid="media-upload-submit-btn">
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl px-6">
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Media Filter Tabs */}
        <Tabs defaultValue="all" className="mb-8" onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm p-1 rounded-2xl flex-wrap h-auto gap-1">
            <TabsTrigger value="all" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="media-filter-all">
              All ({mediaCounts.all})
            </TabsTrigger>
            <TabsTrigger value="image" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="media-filter-image">
              <Image className="w-4 h-4 mr-1" />
              Images ({mediaCounts.image})
            </TabsTrigger>
            <TabsTrigger value="video" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="media-filter-video">
              <Video className="w-4 h-4 mr-1" />
              Videos ({mediaCounts.video})
            </TabsTrigger>
            <TabsTrigger value="audio" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="media-filter-audio">
              <Music className="w-4 h-4 mr-1" />
              Audio ({mediaCounts.audio})
            </TabsTrigger>
            <TabsTrigger value="document" className="rounded-xl px-4 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="media-filter-document">
              <FileText className="w-4 h-4 mr-1" />
              Documents ({mediaCounts.document})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Media Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} className="aspect-video animate-pulse bg-gray-200 rounded-2xl"></Card>
            ))}
          </div>
        ) : filteredMedia.length === 0 ? (
          <Card className="p-16 text-center rounded-3xl border-dashed">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <Image className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Media Found</h3>
            <p className="text-gray-500 mb-6">
              {activeTab === "all" 
                ? "Upload your first media file to get started!" 
                : `No ${activeTab} files uploaded yet.`}
            </p>
            <Button onClick={() => setShowUploadForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
              <Upload className="w-4 h-4 mr-2" />
              Upload Media
            </Button>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredMedia.map((item) => (
              <Card 
                key={item.media_id} 
                className="overflow-hidden cursor-pointer rounded-2xl group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                onClick={() => setSelectedMedia(item)}
                data-testid={`media-item-${item.media_id}`}
              >
                <div className="aspect-video bg-gray-100 flex items-center justify-center relative overflow-hidden">
                  {item.media_type === "image" && (
                    <img src={item.file_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  )}
                  {item.media_type === "video" && (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-violet-700 flex items-center justify-center">
                      <Video className="w-12 h-12 text-white" />
                    </div>
                  )}
                  {item.media_type === "audio" && (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <Music className="w-12 h-12 text-white" />
                    </div>
                  )}
                  {item.media_type === "document" && (
                    <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                      <FileText className="w-12 h-12 text-white" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
                      item.media_type === 'image' ? 'bg-emerald-500/90 text-white' :
                      item.media_type === 'video' ? 'bg-purple-500/90 text-white' :
                      item.media_type === 'audio' ? 'bg-blue-500/90 text-white' :
                      'bg-amber-500/90 text-white'
                    }`}>
                      {item.media_type}
                    </span>
                  </div>
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h4 className="font-semibold text-gray-900 line-clamp-1 group-hover:text-emerald-700 transition-colors">{item.title}</h4>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-gray-500">{formatFileSize(item.file_size)}</span>
                    <span className="text-gray-400">{format(new Date(item.created_at), "MMM d")}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Media Detail Modal */}
        {selectedMedia && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMedia(null)}
          >
            <Card 
              className="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-3xl border-0 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                      selectedMedia.media_type === 'image' ? 'bg-emerald-100 text-emerald-700' :
                      selectedMedia.media_type === 'video' ? 'bg-purple-100 text-purple-700' :
                      selectedMedia.media_type === 'audio' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedMedia.media_type}
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-gray-900">{selectedMedia.title}</h3>
                    <p className="text-gray-500 mt-1">{selectedMedia.description || "No description provided"}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedMedia(null)} className="rounded-full">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Media Preview */}
                <div className="aspect-video bg-gray-900 rounded-2xl mb-6 flex items-center justify-center overflow-hidden">
                  {selectedMedia.media_type === "image" && (
                    <img src={selectedMedia.file_url} alt={selectedMedia.title} className="w-full h-full object-contain" />
                  )}
                  {selectedMedia.media_type === "video" && (
                    <video src={selectedMedia.file_url} controls className="w-full h-full" />
                  )}
                  {selectedMedia.media_type === "audio" && (
                    <div className="text-center p-8">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Music className="w-12 h-12 text-white" />
                      </div>
                      <audio src={selectedMedia.file_url} controls className="w-full max-w-md" />
                    </div>
                  )}
                  {selectedMedia.media_type === "document" && (
                    <div className="text-center p-8">
                      <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <FileText className="w-12 h-12 text-white" />
                      </div>
                      <p className="text-white font-medium">{selectedMedia.file_name}</p>
                    </div>
                  )}
                </div>

                {/* Media Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Type</p>
                    <p className="font-semibold text-gray-900 capitalize">{selectedMedia.media_type}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Size</p>
                    <p className="font-semibold text-gray-900">{formatFileSize(selectedMedia.file_size)}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Uploaded</p>
                    <p className="font-semibold text-gray-900">{format(new Date(selectedMedia.created_at), "MMM d, yyyy")}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">By</p>
                    <p className="font-semibold text-gray-900 truncate">{selectedMedia.uploaded_by_name}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    asChild
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                    data-testid="media-view-btn"
                  >
                    <a href={selectedMedia.file_url} target="_blank" rel="noopener noreferrer">
                      <Eye className="w-4 h-4 mr-2" />
                      View Full Size
                    </a>
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleDelete(selectedMedia.media_id)}
                    className="rounded-xl"
                    data-testid="media-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Media
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMediaGallery;
